import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Mail, PhoneCall, MapPin } from "lucide-react";
import api from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received — we'll be in touch shortly.");
    } catch (e) {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="overline text-emerald-800 mb-3">Contact</p>
        <h1 className="font-display text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-[1.05]">
          Talk to a supply specialist.
        </h1>
        <p className="mt-5 text-slate-600 max-w-md">
          Bulk pricing, custom quotes, purchase-order onboarding — our team responds within one business day.
        </p>

        <div className="mt-10 space-y-4">
          <Info icon={Mail} label="Email" value="sales@medsupply.pro" />
          <Info icon={PhoneCall} label="Phone" value="+1 (800) 555-0142" />
          <Info icon={MapPin} label="Headquarters" value="Chicago, IL · Distribution nationwide" />
        </div>
      </div>

      <div className="lg:col-span-7">
        {done ? (
          <div data-testid={CONTACT.success} className="bg-white border border-emerald-200 rounded-sm p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-700 mx-auto mb-4" />
            <p className="font-display text-2xl font-black text-slate-900">Thanks — we've got it.</p>
            <p className="text-slate-500 mt-2">A specialist will follow up within one business day.</p>
          </div>
        ) : (
          <form data-testid={CONTACT.form} onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input data-testid={CONTACT.name} required value={form.name} onChange={(e) => set("name", e.target.value)} className={cls} />
              </Field>
              <Field label="Company / facility">
                <input data-testid={CONTACT.company} value={form.company} onChange={(e) => set("company", e.target.value)} className={cls} />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input data-testid={CONTACT.email} type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={cls} />
              </Field>
              <Field label="Phone">
                <input data-testid={CONTACT.phone} value={form.phone} onChange={(e) => set("phone", e.target.value)} className={cls} />
              </Field>
            </div>
            <Field label="How can we help?" required>
              <textarea data-testid={CONTACT.message} required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className={cls} placeholder="Volume, SKUs, timing, delivery zip codes…" />
            </Field>
            <button
              data-testid={CONTACT.submit}
              disabled={busy}
              className="w-full py-3.5 bg-emerald-900 text-white font-semibold text-sm rounded-sm hover:bg-emerald-800 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const cls =
  "w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-900/20 focus:border-emerald-900";

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-emerald-900" />
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-slate-900 font-medium">{value}</p>
      </div>
    </div>
  );
}
